/* English content. Mirrors de.js field-for-field so the two languages never drift in structure. */

const nav = {
  experience: "Experience",
  education: "Education",
  projects: "Projects",
  skills: "Skills",
  achievements: "Achievements",
  contact: "Contact Me"
};

const greeting = {
  title: "Hi, I'm Talal Ahmed",
  subTitle:
    "A full-stack Software Engineer who owns a feature end to end, from a FastAPI backend processing millions of requests a day to the React or Next.js interface people actually click. Currently finishing an M.Sc. in Computer and Systems Engineering at TU Ilmenau, Germany.",
  displayGreeting: true,
  contactButtonText: "Contact me"
};

const educationInfo = {
  display: true,
  schools: [
    {
      schoolName: "Technische Universität Ilmenau",
      logo: require("../assets/images/tu-ilmenau.png"),
      subHeader: "M.Sc. Computer and Systems Engineering",
      duration: "November 2024 - October 2026",
      desc: "Thesis: Synchronization of Web-Based Petri Nets Multi-User Token Game (concluding October 2026)",
      descBullets: [
        "Building the entire user-facing interface in HTML, CSS, and Vanilla JavaScript (breakpoint dialog UI, real-time token marking display), backed by a PHP socket handler, no frontend framework."
      ]
    },
    {
      schoolName: "National University of Computer and Emerging Sciences",
      logo: require("../assets/images/nuces.png"),
      subHeader: "Bachelor of Science in Computer Science",
      duration: "August 2019 - June 2023",
      desc: "Graduated with a Grade of 1.9",
      descBullets: [
        "Honored with the Dean's List Award for the Fall 2020, Fall 2021, and Spring 2023 semesters",
        "Coursework: OOP, Data Structures, Algorithms, Operating Systems, Database, Software Engineering, Software Design & Analysis, MLOps, Cloud Computing, and Web Engineering"
      ]
    }
  ]
};

const workExperiences = {
  display: true,
  experience: [
    {
      role: "Software Engineer, Working Student",
      company: "Markt-Pilot GmbH",
      companylogo: require("../assets/images/markt.jpeg"),
      date: "December 2024 - Present",
      desc: "Backend Software Engineer building a production extraction platform handling ~5 million requests/day",
      descBullets: [
        "Designed and shipped several FastAPI + PostgreSQL REST endpoints within a production extraction platform that handles roughly 5 million requests a day.",
        "Built and maintained core services (Request Service, Healthcheck, Sonar) using async Python and Temporal workflow orchestration, recovering automatically from partial failures.",
        "Built an LLM-powered classification system to catch anomalous and blocked content in the pipeline, with an uncertainty layer that quarantines low-confidence outputs instead of letting them propagate downstream.",
        "Improved extraction accuracy by up to 30% through ZeroShot method improvements, and led anti-bot detection work feeding labeled data into the team's ML blockage model.",
        "Built observability tooling, error labeling, HTML/source snapshots to Azure Blob Storage, and Sentry alert cleanup, so production failures were actually debuggable.",
        "Improved the team's frontend observability dashboard, shipping new features and fixing UI components in TypeScript and Angular.",
        "Diagnosed and fixed a cluster of production bugs (schema mismatches, race conditions, price-extraction issues) and restored two broken CI pipelines that had stopped catching regressions.",
        "Reviewed pull requests, coordinated with Operations and freelance contributors, and tracked work through Jira and Azure DevOps in an agile team."
      ]
    },
    {
      role: "Software Engineer, Full-Stack",
      company: "Arbisoft",
      companylogo: require("../assets/images/arbisoft.png"),
      date: "June 2023 - November 2024",
      desc: "Full-stack engineer across two products: LitmusTest, a code-assessment platform used by 8,000+ students, and MyCavago, a customer-facing booking app rebuilt from scratch",
      descBullets: [
        "Manual grading didn't scale for 8,000+ LitmusTest students. Built Rubric Automation, a rule-based code-evaluation engine with deterministic, testable scoring logic, cutting manual grading time by 75%.",
        "LitmusTest needed to stay responsive under concurrent exam load. Owned the React.js/TypeScript frontend and PostgreSQL-backed API together, focused on reliability during peak usage, keeping the platform stable for 8,000+ concurrent students during live exams.",
        "MyCavago's customer-facing frontend needed a full rebuild. Rewrote the app from scratch using Next.js, TypeScript, and TanStack Query, delivering a modern, responsive UI/UX for equestrian activity bookings, owned from requirements through deployment.",
        "MyCavago's authentication and booking flows needed to hold up under real customer traffic. Built the authentication and activity booking modules, ensuring a seamless user experience and resolving critical production bugs under high concurrent load.",
        "Arbisoft needed a graduate hiring site built fast, no framework overhead. Designed and built the Fresh Grad Hiring 2024 website end to end using jQuery and vanilla JavaScript, hand-written HTML/CSS, zero framework dependency.",
        "Cross-functional coordination was needed across both products. Managed work through Jira, reviewed PRs, and collaborated directly with product, design, and client teams."
      ]
    }
  ]
};

const bigProjects = {
  title: "Projects",
  subtitle: "CURRENT & PREVIOUS PROJECTS THAT I HAVE WORKED ON",
  projects: [
    {
      image: require("../assets/images/road-intelligence.png"),
      projectName: "Road Intelligence",
      projectDesc:
        "A self-built study platform covering all 1,030 official German Class B driving-theory questions, with adaptive spaced-repetition, a real-time readiness score, and bilingual EN/DE support.",
      footerLink: [{name: "Visit Website", url: "https://www.fuehrerscheintest.app"}]
    },
    {
      image: require("../assets/images/cavago.png"),
      projectName: "Cavago",
      projectDesc:
        "Rewrote the frontend from scratch with Next.js, TypeScript, and TanStack Query for all kinds of equestrian businesses.",
      footerLink: [{name: "Visit Website", url: "https://mycavago.com/home"}]
    },
    {
      image: require("../assets/images/litmus.png"),
      projectName: "Litmustest",
      projectDesc:
        "Code assessment platform serving 8,000+ concurrent students; owned the React/TypeScript frontend end to end.",
      footerLink: [{name: "Visit Website", url: "https://litmustest.io/"}]
    },
    {
      image: require("../assets/images/fgh.png"),
      projectName: "Fresh Grad Hiring 2024",
      projectDesc:
        "Arbisoft's graduate hiring site, designed and built end to end with jQuery and Vanilla JavaScript: semantic HTML, hand-written CSS, zero framework dependency.",
      footerLink: [{name: "Visit Website", url: "https://freshgradhiring.arbisoft.com"}]
    },
    {
      image: require("../assets/images/qwizard.png"),
      projectName: "QWizard",
      projectDesc:
        "An Autonomous Quiz Generation and Evaluation System developed using Python, FastAPI, Docker, Next.js and hosted on Microsoft Azure.",
      footerLink: [{name: "Visit Website", url: "https://qwizard-next.vercel.app/"}]
    },
    {
      image: require("../assets/images/burger.png"),
      projectName: "Burger Delivery App",
      projectDesc: "A Full Stack Web Application developed using React and hosted using Firebase.",
      footerLink: [{name: "Visit Website", url: "https://burger-builder-a9536.web.app/"}]
    }
  ],
  display: true
};

const skillsShowcase = {
  title: "Skills",
  subTitle:
    "A working set across the whole stack: I've shipped in every one of these, on real, production systems.",
  groups: [
    {
      groupName: "Frontend",
      skills: [
        {skillName: "HTML5", fontAwesomeClassname: "fab fa-html5"},
        {skillName: "CSS3", fontAwesomeClassname: "fab fa-css3-alt"},
        {skillName: "JavaScript", fontAwesomeClassname: "fab fa-js"},
        {skillName: "React.js", fontAwesomeClassname: "fab fa-react"},
        {skillName: "Next.js", fontAwesomeClassname: "fas fa-layer-group"},
        {skillName: "TypeScript", fontAwesomeClassname: "fas fa-code"},
        {skillName: "TanStack Query", fontAwesomeClassname: "fas fa-arrows-rotate"},
        {skillName: "jQuery", fontAwesomeClassname: "fab fa-jquery"}
      ]
    },
    {
      groupName: "Backend",
      skills: [
        {skillName: "Python", fontAwesomeClassname: "fab fa-python"},
        {skillName: "FastAPI", fontAwesomeClassname: "fas fa-bolt"},
        {skillName: "Django", fontAwesomeClassname: "fas fa-server"},
        {skillName: "PHP", fontAwesomeClassname: "fab fa-php"}
      ]
    },
    {
      groupName: "Data & APIs",
      skills: [
        {skillName: "PostgreSQL", fontAwesomeClassname: "fas fa-database"},
        {skillName: "SQL", fontAwesomeClassname: "fas fa-table"},
        {skillName: "REST APIs", fontAwesomeClassname: "fas fa-diagram-project"},
        {skillName: "Pydantic", fontAwesomeClassname: "fas fa-shield-halved"}
      ]
    },
    {
      groupName: "Cloud & DevOps",
      skills: [
        {skillName: "Docker", fontAwesomeClassname: "fab fa-docker"},
        {skillName: "Microsoft Azure", fontAwesomeClassname: "fab fa-microsoft"},
        {skillName: "CI/CD", fontAwesomeClassname: "fas fa-infinity"},
        {skillName: "Git", fontAwesomeClassname: "fab fa-git-alt"}
      ]
    },
    {
      groupName: "AI & Automation",
      skills: [
        {skillName: "Generative AI", fontAwesomeClassname: "fas fa-brain"},
        {skillName: "NLP", fontAwesomeClassname: "fas fa-language"},
        {skillName: "Selenium", fontAwesomeClassname: "fas fa-robot"},
        {skillName: "Temporal Workflow", fontAwesomeClassname: "fas fa-clock"}
      ]
    }
  ],
  languages: {
    groupName: "Languages",
    items: [
      {name: "English", level: "C1, Proficient"},
      {name: "German", level: "A2, Elementary, working toward B1"}
    ]
  },
  display: true
};

const achievementSection = {
  title: "Achievements And Certifications",
  subtitle: "Achievements, certifications, award letters, and some cool stuff that I have done!",
  achievementsCards: [
    {
      title: "Microsoft Azure Fundamentals (AZ-900)",
      subtitle: "Completed the Microsoft Azure Fundamentals Certification",
      image: require("../assets/images/azure.png"),
      imageAlt: "Azure Fundamentals Logo",
      footerLink: [
        {name: "Certification", url: "https://www.credly.com/badges/5cf97433-dd61-4277-879f-28d0e47a1470"},
      ]
    },
    {
      title: "PCEP",
      subtitle: "Certified Entry-Level Python Programmer",
      image: require("../assets/images/pcep.png"),
      imageAlt: "[PCEP-30-02] PCEP - Certified Entry-Level Python Programmer",
      footerLink: [
        {name: "Certification Official", url: "https://verify.openedg.org/?id=jUyu.Ar0a.6wQ7"},
        {name: "Detailed Result", url: "https://drive.google.com/file/d/1GJnsnGOOvbBFAACJloJuBDzrg0UyxZ71/view"}
      ]
    }
  ],
  display: true
};

const contactInfo = {
  title: "Contact Me",
  subtitle: "Discuss a project or just want to say hi? My inbox is open for all.",
  number: "+49-176-13722237",
  email_address: "talalahmed252@gmail.com",
  display: true
};

const content = {
  nav,
  greeting,
  educationInfo,
  workExperiences,
  bigProjects,
  skillsShowcase,
  achievementSection,
  contactInfo,
  isHireable: true
};

export default content;
